import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import DemoBookingCard from '../components/DemoBookingCard';
import DemoBookingStats from '../components/DemoBookingStats';
import { demoBookingService } from '../services/demoBookingService';
import { Calendar, Loader2 } from 'lucide-react';

const DemoBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseAction, setResponseAction] = useState(null);
  const [tutorResponse, setTutorResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBookings();
  }, [activeTab]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const filters = activeTab !== 'all' ? { status: activeTab } : {};
      const response = await demoBookingService.getDemoBookings(filters);
      if (response.success) {
        setBookings(response.data.bookings);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch demo bookings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    setSelectedBooking(booking);
    setResponseAction('confirmed');
    setTutorResponse('');
    setShowResponseModal(true);
  };

  const handleReject = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    setSelectedBooking(booking);
    setResponseAction('cancelled');
    setTutorResponse('');
    setShowResponseModal(true);
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedBooking || !responseAction) return;

    setSubmitting(true);
    try {
      const response = await demoBookingService.updateBookingStatus(
        selectedBooking.id,
        responseAction,
        tutorResponse
      );

      if (response.success) {
        toast({
          title: 'Success',
          description: `Booking ${responseAction === 'confirmed' ? 'accepted' : 'rejected'} successfully`,
        });
        setShowResponseModal(false);
        fetchBookings();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update booking',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getTabCount = (status) => {
    return bookings.filter(b => b.status === status).length;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Demo Session Requests</h1>
        <p className="text-gray-600">Manage your demo session bookings</p>
      </div>

      <div className="mb-8">
        <DemoBookingStats />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">
            Pending {activeTab === 'all' && bookings.filter(b => b.status === 'pending').length > 0 && 
              `(${bookings.filter(b => b.status === 'pending').length})`}
          </TabsTrigger>
          <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No bookings found</h3>
            <p className="text-gray-500">
              {activeTab === 'all' 
                ? 'You have no demo session requests yet' 
                : `No ${activeTab} bookings`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bookings.map((booking) => (
              <DemoBookingCard
                key={booking.id}
                booking={booking}
                onAccept={handleAccept}
                onReject={handleReject}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Customer Information</h4>
                  <p className="text-sm"><strong>Name:</strong> {selectedBooking.customer.name}</p>
                  <p className="text-sm"><strong>Phone:</strong> {selectedBooking.customer.phone}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Student Information</h4>
                  <p className="text-sm"><strong>Name:</strong> {selectedBooking.student.name}</p>
                  <p className="text-sm"><strong>Class:</strong> {selectedBooking.student.class}</p>
                  <p className="text-sm"><strong>Board:</strong> {selectedBooking.student.board}</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Session Details</h4>
                <p className="text-sm"><strong>Date:</strong> {new Date(selectedBooking.demo_date).toLocaleDateString()}</p>
                <p className="text-sm"><strong>Time:</strong> {selectedBooking.demo_time}</p>
                <p className="text-sm"><strong>Status:</strong> <span className="capitalize">{selectedBooking.status}</span></p>
              </div>
              {selectedBooking.notes && (
                <div>
                  <h4 className="font-semibold mb-2">Customer Notes</h4>
                  <p className="text-sm bg-gray-50 p-3 rounded">{selectedBooking.notes}</p>
                </div>
              )}
              {selectedBooking.tutor_response && (
                <div>
                  <h4 className="font-semibold mb-2">Your Response</h4>
                  <p className="text-sm bg-blue-50 p-3 rounded">{selectedBooking.tutor_response}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Responded at: {new Date(selectedBooking.responded_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {responseAction === 'confirmed' ? 'Accept' : 'Reject'} Demo Request
            </DialogTitle>
            <DialogDescription>
              {responseAction === 'confirmed' 
                ? 'Add a message to confirm this demo session' 
                : 'Please provide a reason for rejection'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder={responseAction === 'confirmed' 
                ? 'e.g., Looking forward to the session!' 
                : 'e.g., Not available at this time'}
              value={tutorResponse}
              onChange={(e) => setTutorResponse(e.target.value)}
              rows={4}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowResponseModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitResponse}
                disabled={submitting}
                className={responseAction === 'confirmed' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  responseAction === 'confirmed' ? 'Accept' : 'Reject'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoBookings;
